import { useCallback } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams, useResolvedPath } from 'react-router-dom';
import { PayDashboardActivity, SyncFlowEnum } from '@melio/ap-activities';
import { LocalStorageKeys } from '@melio/local-storage';
import { OriginFlow } from '@melio/platform-analytics';
import { AccountingPlatformSlug, useAccountingPlatforms } from '@melio/platform-api';

import { usePartnerConfig } from '@/hooks/partners';
import { usePartnerLocalStorage } from '@/hooks/partners/usePartnerLocalStorage';
import { useRouter } from '@/hooks/router.hooks';
import { useActiveScreen } from '@/hooks/useActiveScreen';
import { ActiveFlowEnum, ScreensEnum } from '@/store/app/app.types';
import { VendorDrawer } from '@/widgets/VendorDrawer/VendorDrawer.widget';
import { useAccountingPlatformConnect } from '../../hooks/accountingPlatforms.hooks';
import { useOnSyncWithQb } from '../../hooks/useAddNewBill.hooks';
import { getQboAccountingId, getXeroAccountingPlatform } from '../../utils/accountingPlatform.utils';

export const NewPayDashboardRoute = () => {
  const navigate = useNavigate();
  const localStorage = usePartnerLocalStorage();
  const { partnerConfig } = usePartnerConfig();
  useActiveScreen(ScreensEnum.payDashboard, ActiveFlowEnum.payDashboard);
  const {
    goToAddNewBill,
    goToAddNewVendor,
    goToReviewScannedInvoice,
    goToEditPayment,
    goToJustPay,
    goToEditBill,
    generateNPEDashboardLink,
    goToRetryFailedToCollectPayment,
    goToRetryFailedToDeliverPayment,
    goToRefundPayment,
    goToVoidAndRefundPayment,
    goToVoidAndResendPayment,
    goToSettingsAccountSoftware,
    goToApprovePaymentRequest,
    goToEBillsSubscription,
    goToAutoPaymentActivation,
    goToEditBillSubscription,
    goToAddNewBillManual,
    goToAddNewBillUpload,
  } = useRouter();
  const { pathname } = useLocation();

  const onOpenFreeAchPromotionModal = useCallback(() => {
    if (localStorage.getItem(LocalStorageKeys.freeAchPromotionModalSeen)) {
      return false;
    }
    localStorage.setItem(LocalStorageKeys.freeAchPromotionModalSeen, 'true');
    return true;
  }, [localStorage]);

  const handlePayPaymentRequest = (id: string) => {
    goToApprovePaymentRequest({ paymentRequestId: id, returnUrl: pathname });
  };

  const handleAddNewBill = ({
    returnUrl,
    vendorId,
    originFlow,
    amount,
  }: {
    returnUrl: string;
    vendorId?: string;
    originFlow?: OriginFlow;
    amount?: string;
  }) => {
    if (vendorId) {
      goToAddNewBill({ originFlow: OriginFlow.VendorsFlow, returnUrl, vendorId, amount });
    } else if (originFlow) {
      goToAddNewBill({ originFlow, returnUrl });
    } else {
      goToAddNewBill({ originFlow: OriginFlow.BillPage, returnUrl });
    }
  };

  const handleAddNewBillManual = ({ returnUrl, vendorId }: { returnUrl: string; vendorId?: string }) => {
    if (vendorId) {
      goToAddNewBillManual({ originFlow: OriginFlow.VendorsFlow, returnUrl, vendorId });
    } else {
      goToAddNewBillManual({ originFlow: OriginFlow.BillPage, returnUrl });
    }
  };

  const handleAddNewBillUpload = ({
    returnUrl,
    vendorId,
    files,
  }: {
    returnUrl: string;
    vendorId?: string;
    files?: File[];
  }) => {
    if (vendorId) {
      goToAddNewBillUpload({ originFlow: OriginFlow.VendorsFlow, returnUrl, vendorId, files });
    } else {
      goToAddNewBillUpload({ originFlow: OriginFlow.BillPage, returnUrl, files });
    }
  };

  const handleEBillSubscribe = ({ vendorId }: { vendorId: string }) => {
    goToEBillsSubscription(vendorId);
  };

  const handleAutoPayActivation = ({ vendorId }: { vendorId: string }) => {
    goToAutoPaymentActivation(vendorId);
  };

  const handleOnCloseDrawer = (tabName: string) => {
    const params = new URLSearchParams(window.location.search);

    const link = generateNPEDashboardLink(tabName);

    navigate({
      pathname: link,
      search: params.toString(),
    });
  };

  const handleViewPayment = ({ paymentId }: { paymentId?: string }) => {
    const link = `${generateNPEDashboardLink('payments')}/${paymentId}`;

    navigate({
      pathname: link,
    });
  };

  const { data: accountingPlatformData } = useAccountingPlatforms();
  const qboAccountingId = getQboAccountingId(accountingPlatformData);
  const xeroAccountingPlatform = getXeroAccountingPlatform(accountingPlatformData);
  const { onSyncWithQb: onConnectQuickbooks, isLoading: isQuickbooksConnecting } = useOnSyncWithQb(qboAccountingId);
  const { isLoading: isXeroConnecting, connectToAccountingPlatform: onConnectXero } = useAccountingPlatformConnect({
    flowToReturn: SyncFlowEnum.NewBill,
    accountingPlatformId: xeroAccountingPlatform?.id,
  });
  const isAccountingPlatformConnecting = isQuickbooksConnecting || isXeroConnecting;
  const onConnectAccountingPlatform = (accountingPlatformSlug: AccountingPlatformSlug) => {
    if (accountingPlatformSlug === AccountingPlatformSlug.QuickBooksOnline) {
      onConnectQuickbooks();
    } else if (accountingPlatformSlug === AccountingPlatformSlug.Xero) {
      onConnectXero();
    }
  };

  return (
    <>
      <PayDashboardActivity
        onAddNewBill={handleAddNewBill}
        onAddNewBillManual={handleAddNewBillManual}
        onAddNewBillUpload={handleAddNewBillUpload}
        onEditBill={goToEditBill}
        onAddNewVendor={goToAddNewVendor}
        onEBillSubscribe={handleEBillSubscribe}
        onAutoPayActivation={handleAutoPayActivation}
        onPayPaymentRequest={handlePayPaymentRequest}
        onReviewScannedInvoice={({ id, returnUrl }) => goToReviewScannedInvoice({ scannedInvoiceId: id, returnUrl })}
        onEditPayment={goToEditPayment}
        onAddInstantBill={(args) =>
          goToJustPay({ returnUrl: pathname, vendorId: args?.vendorId, amount: args?.amount })
        }
        onRetryFailedToDeliverPayment={goToRetryFailedToDeliverPayment}
        onRetryFailedToCollectPayment={goToRetryFailedToCollectPayment}
        onRefundPayment={goToRefundPayment}
        onVoidAndRefundPayment={goToVoidAndRefundPayment}
        onVoidAndResendPayment={goToVoidAndResendPayment}
        goToSettingsAccountSoftware={goToSettingsAccountSoftware}
        onConnectAccountingPlatform={onConnectAccountingPlatform}
        onViewPayment={({ id: paymentId }) => {
          handleViewPayment({ paymentId });
        }}
        onOpenFreeAchPromotionModal={onOpenFreeAchPromotionModal}
        onEditBillSubscription={goToEditBillSubscription}
        tabsVariant={partnerConfig.tabsVariant || 'default'}
        isAccountingPlatformConnecting={isAccountingPlatformConnecting}
      />
      <Routes>
        <Route
          path="/:tab/vendor/:vendorId"
          element={
            <VendorDrawerRoute onClose={handleOnCloseDrawer} onEditBillSubscription={goToEditBillSubscription} />
          }
        />
      </Routes>
    </>
  );
};

const VendorDrawerRoute = ({
  onClose,
  onEditBillSubscription,
}: {
  onClose: (tabName: string) => void;
  onEditBillSubscription: ({ id, returnUrl }: { id: string; returnUrl: string }) => void;
}) => {
  const { tab } = useParams();
  const { vendorId } = useParams();

  if (!vendorId) {
    throw new Error(`could not get vendorId from route params.`);
  }

  const { pathname: returnUrl } = useResolvedPath('');

  const onEditBillSubscriptionWithReturnUrl = ({ id }: { id: string }) => {
    onEditBillSubscription({ id, returnUrl });
  };

  return (
    <VendorDrawer
      vendorId={vendorId}
      onClose={() => {
        if (tab) onClose(tab);
      }}
      onEditBillSubscription={onEditBillSubscriptionWithReturnUrl}
    />
  );
};
